/**
 * The hotbar — ten numbered cells on the bottom edge of a fight, plus the red
 * OUT OF AMMO notice that shares its module.
 *
 * One slice of the base-view stylesheet. See `styles/index.ts`.
 *
 * NO BACKTICKS anywhere in this file, comments included: the export below is
 * one template literal and a stray backtick terminates it. That has broken the
 * build twice, both times with esbuild pointing at a line ten rows away from
 * the cause. Write class names in plain text.
 */
export default `
/* ─── Hotbar ────────────────────────────────────────────────────────────────
   Layers sit with the duel HUD (30+), not with the settlement's panels: the
   arena mounts on its own and has no windows to fight with. */
.bv-hotbar {
  position: absolute; inset: auto 0 0 0;
  z-index: 32;
  pointer-events: none;
  font-family: var(--font-technical, monospace);
}

.bv-hotbar-cells {
  /* The bar itself IS clickable — the wrapper above is not, so the rest of the
     bottom edge stays free for the camera drag. */
  pointer-events: auto;
  display: flex; justify-content: center; gap: 4px;
  padding: 8px 10px 10px;
  margin: 0 auto;
  width: max-content; max-width: calc(100% - 16px);
}

.bv-hotbar-cell {
  position: relative;
  flex: 0 0 auto;
  /* 48px is a deliberate touch target, not a look: Janeks asked for the cells
     to work by touch on mobile, and anything under ~44px fails a thumb. */
  width: 48px; height: 48px;
  padding: 0;
  display: grid; place-items: center;
  background: linear-gradient(180deg, rgba(34,28,23,0.94), rgba(20,16,13,0.92));
  border: 1px solid var(--brass, #8a7345);
  border-radius: var(--radius-sm, 3px);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.05);
  color: var(--bone, #e8ddc8);
  cursor: pointer;
  transition: border-color 120ms linear, box-shadow 120ms linear, transform 80ms ease-out;
}
.bv-hotbar-cell:hover:not(:disabled) { border-color: var(--bone, #e8ddc8); }
.bv-hotbar-cell:active:not(:disabled) { transform: translateY(1px); }

/* Held. A brass glow rather than a fill, so the item art stays readable.
   BRASS and not rust: rust is this palette's danger colour and is what the
   error state below uses, so a held weapon tinted with it would read as a
   warning and the two states would be the same shape in the same hue. */
.bv-hotbar-cell.is-active {
  border-color: var(--brass-hot, #ffbd5c);
  box-shadow: 0 0 0 1px var(--brass-hot, #ffbd5c), 0 0 12px rgba(255,189,92,0.35),
    inset 0 1px 0 rgba(255,255,255,0.06);
}

/* Refused to fire — see hotbar.ts's flashError. Overrides the active ring on
   purpose: it is always the HELD cell that goes red, so the two would
   otherwise stack and the red would read as orange. */
.bv-hotbar-cell.is-error {
  border-color: var(--rust, #a8452c);
  box-shadow: 0 0 0 1px var(--rust, #a8452c), 0 0 14px rgba(168,69,44,0.6);
  animation: bv-hotbar-shake 180ms ease-in-out 2;
}
@keyframes bv-hotbar-shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-2px); }
  75% { transform: translateX(2px); }
}
@media (prefers-reduced-motion: reduce) {
  .bv-hotbar-cell.is-error { animation: none; }
}

/* An empty cell is drawn, dimmed, and dead — the positions are fixed, so the
   gaps have to be visible or the numbering looks arbitrary. */
.bv-hotbar-cell.is-empty {
  background: rgba(16,13,11,0.6);
  border-color: rgba(138,115,69,0.35);
  cursor: default;
}

.bv-hotbar-key {
  position: absolute; top: 1px; left: 3px;
  font-size: 10px; line-height: 1;
  color: var(--brass, #8a7345);
}
.bv-hotbar-cell.is-active .bv-hotbar-key { color: var(--brass-hot, #ffbd5c); }

.bv-hotbar-art { width: 36px; height: 36px; object-fit: contain; display: block; }

.bv-hotbar-qty {
  position: absolute; right: 3px; bottom: 1px;
  font-size: 10px; line-height: 1;
  color: var(--bone, #e8ddc8);
  text-shadow: 0 1px 2px rgba(0,0,0,0.9);
}

/* Rounds left, under the held weapon only. Centred on the bottom edge so it
   cannot collide with the stack badge in the corner. */
.bv-hotbar-ammo {
  position: absolute; left: 0; right: 0; bottom: -1px;
  font-size: 11px; line-height: 1.3;
  text-align: center;
  color: var(--bone, #e8ddc8);
  background: rgba(12,10,8,0.8);
  text-shadow: 0 1px 2px rgba(0,0,0,0.9);
}

/* ─── The heal cast bar ─────────────────────────────────────────────────────
   Above the cells rather than in the middle of the screen: the OUT OF AMMO
   notice owns the centre, and a bar that only appears for three to eight
   seconds should not fight it for the same pixels. */
.bv-hotbar-cast {
  width: min(260px, calc(100% - 24px));
  margin: 0 auto;
  opacity: 0;
  transition: opacity 120ms linear;
  pointer-events: none;
}
.bv-hotbar-cast.is-shown { opacity: 1; }

.bv-hotbar-cast-label {
  font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase;
  color: var(--bone, #e8ddc8);
  text-shadow: 0 1px 3px rgba(0,0,0,0.9);
  margin-bottom: 3px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.bv-hotbar-cast-track {
  position: relative; height: 6px;
  background: rgba(27,22,19,0.9);
  border: 1px solid rgba(0,0,0,0.5);
  border-radius: 2px; overflow: hidden;
}
/* MOSS, the palette's success colour — the same green the health bar fills
   with, because this bar is a heal arriving and nothing else. */
.bv-hotbar-cast-fill {
  position: absolute; inset: 0 auto 0 0; width: 0;
  background: linear-gradient(180deg, var(--moss, #74854a), var(--moss-dark, #52602f));
  transition: width 90ms linear;
}

/* ─── OUT OF AMMO ───────────────────────────────────────────────────────────
   Janeks asked for it in the MIDDLE of the screen, not on the bar: the bar is
   at the edge of vision during a fight and a message there is a message nobody
   reads. Anchored to the viewport rather than to the bar. */
.bv-hotbar-notice {
  position: fixed; left: 50%; top: 44%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  opacity: 0;
  font-family: var(--font-display, sans-serif);
  font-size: clamp(20px, 4vw, 34px);
  letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--rust, #a8452c);
  text-shadow: 0 2px 10px rgba(0,0,0,0.9), 0 0 18px rgba(168,69,44,0.5);
  transition: opacity 120ms linear;
}
.bv-hotbar-notice.is-shown { opacity: 1; }
`;
